const API_BASE = "/api/v1";

let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function refreshAccessToken(): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/auth/token`, {
      method: "POST",
      credentials: "include",
    });
    return res.ok;
  } catch {
    return false;
  }
}

async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const url = `${API_BASE}${path}`;
  const defaultHeaders: Record<string, string> = {};

  // Only set Content-Type for requests that have a body
  if (options.body) {
    defaultHeaders["Content-Type"] = "application/json";
  }

  const res = await fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...defaultHeaders, ...options.headers },
  });

  if (res.status === 401) {
    if (!isRefreshing) {
      isRefreshing = true;
      refreshPromise = refreshAccessToken().finally(() => {
        isRefreshing = false;
        refreshPromise = null;
      });
    }

    const refreshed = await refreshPromise;

    if (refreshed) {
      const retryRes = await fetch(url, {
        ...options,
        credentials: "include",
        headers: { ...defaultHeaders, ...options.headers },
      });

      if (!retryRes.ok) {
        const error = await retryRes.json();
        throw new ApiError(
          retryRes.status,
          error.error?.code ?? "UNKNOWN",
          error.error?.message ?? "An unexpected error occurred",
        );
      }

      // Handle 204 No Content
      if (retryRes.status === 204) {
        return undefined as T;
      }

      return retryRes.json() as Promise<T>;
    }

    // Refresh failed -- let caller handle 401 (ProtectedRoute will redirect)
    throw new ApiError(401, "SESSION_EXPIRED", "Session expired. Please log in again.");
  }

  if (!res.ok) {
    let errorBody: { error?: { code?: string; message?: string } };
    try {
      errorBody = await res.json();
    } catch {
      throw new ApiError(res.status, "UNKNOWN", "An unexpected error occurred");
    }
    throw new ApiError(
      res.status,
      errorBody.error?.code ?? "UNKNOWN",
      errorBody.error?.message ?? "An unexpected error occurred",
    );
  }

  // Handle 204 No Content
  if (res.status === 204) {
    return undefined as T;
  }

  return res.json() as Promise<T>;
}

export const api = {
  get<T>(path: string): Promise<T> {
    return apiFetch<T>(path, { method: "GET" });
  },

  post<T>(path: string, body?: unknown): Promise<T> {
    return apiFetch<T>(path, {
      method: "POST",
      body: body ? JSON.stringify(body) : undefined,
    });
  },

  put<T>(path: string, body: unknown): Promise<T> {
    return apiFetch<T>(path, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },

  patch<T>(path: string, body: unknown): Promise<T> {
    return apiFetch<T>(path, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  },

  delete<T = void>(path: string): Promise<T> {
    return apiFetch<T>(path, { method: "DELETE" });
  },
};
